import React from 'react'
import { toast } from 'react-toastify'
import { runSetLayerVisibility } from './artemis-actions/agent-actions'
import { fetchChanges } from './changes-last-called-actions'
import { openModal } from './positioned-modal-state'
import { moveMap } from '../lib/artemis/map-view'
import { shapeBounds, shapeAnchor } from '../lib/map-deep-link'
import { openDrawnMarker } from '../lib/marker-openers'

// Opens an "Open in BaseMap" map deep link (WEB-7109) on the 2D map, once the landing page has
// loaded the user, layers, settings and objects. The format is in lib/map-deep-link.

const OBJECT_ZOOM = 15
const SHAPE_TYPES = ['lines', 'polygons', 'circles', 'tracks']

// How long to wait for the map to exist, and for a linked object to arrive in a sync. An object
// saved by an AI assistant a moment ago may not have synced yet; the startup sync can also still be
// running, in which case fetchChanges returns at once without syncing again.
const MAP_WAIT_MS = 10000
const OBJECT_TRIES = 8
const OBJECT_RETRY_MS = 1500
const MARKER_OPEN_TRIES = 10
const MARKER_OPEN_RETRY_MS = 300

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

const currentMap = getState => {
  const ref = getState().mapReference
  return typeof ref === 'function' ? ref() : null
}

const waitForMap = async getState => {
  for (let waited = 0; waited < MAP_WAIT_MS; waited += 250) {
    const map = currentMap(getState)
    if (map) return map
    await wait(250)
  }
  return currentMap(getState)
}

const sameId = (a, b) => a !== undefined && a !== null && String(a) === String(b)

// The saved object the link names, as { kind, item }, or null when it isn't in the store yet.
export const findLinkedObject = (state, link) => {
  if (link.marker) {
    const item = (state.markers || []).find(m => sameId(m.id, link.marker))
    return item ? { kind: 'marker', item } : null
  }
  if (link.shape) {
    for (const type of SHAPE_TYPES) {
      const item = (state[type] || []).find(s => sameId(s.id, link.shape))
      if (item) return { kind: type, item }
    }
  }
  return null
}

const findAfterSync = async (dispatch, getState, link) => {
  let found = findLinkedObject(getState(), link)
  for (let i = 0; !found && i < OBJECT_TRIES; i++) {
    await dispatch(fetchChanges())
    found = findLinkedObject(getState(), link)
    if (!found) await wait(OBJECT_RETRY_MS)
  }
  return found
}

// Where to put the map for an object: a marker's point, or a shape's bounds.
export const objectView = ({ kind, item }) => {
  if (kind === 'marker') {
    const lng = Number(item.longitude), lat = Number(item.latitude)
    return Number.isFinite(lng) && Number.isFinite(lat) ? { center: [lng, lat], zoom: OBJECT_ZOOM } : null
  }
  const bbox = shapeBounds(item.geoJson)
  if (!bbox) return null
  const [w, s, e, n] = bbox
  return w < e && s < n ? { bbox } : { center: [w, s], zoom: OBJECT_ZOOM }
}

const googleMapsApi = () => (typeof window !== 'undefined' && window.google && window.google.maps) || null

const whenIdle = map => new Promise(resolve => {
  const googleMaps = googleMapsApi()
  if (!googleMaps || !googleMaps.event) return resolve()
  const timer = setTimeout(resolve, 3000)
  googleMaps.event.addListenerOnce(map, 'idle', () => {
    clearTimeout(timer)
    resolve()
  })
})

// Opens the object the way a click on it does: its own click handler selects it and opens its info
// window. Nothing happens when it isn't drawn (its type is hidden, say); the map has still moved to
// it. Markers are drawn as clones, so theirs comes from the marker renderer; shapes carry their map
// object in the store.
const openObject = async ({ kind, item }) => {
  if (kind === 'marker') {
    // The renderer draws markers asynchronously; give it a moment to reach this one.
    for (let i = 0; i < MARKER_OPEN_TRIES && !openDrawnMarker(item.id); i++) await wait(MARKER_OPEN_RETRY_MS)
    return
  }
  const googleMaps = googleMapsApi()
  const mapObj = typeof item.mapObj === 'function' ? item.mapObj() : item.mapObj
  if (!googleMaps || !googleMaps.event || !mapObj) return
  const anchor = shapeAnchor(item.geoJson)
  if (anchor) googleMaps.event.trigger(mapObj, 'click', { latLng: new googleMaps.LatLng(anchor[1], anchor[0]) })
}

// The layers a link asked for that need a plan the user doesn't have. Its Upgrade button does what a
// tap on a locked tile does. A tile can open that straight away, but the link has no click to open it
// on: the upgrade prompt is a window.open of the purchase site, which a browser blocks without one.
const UPGRADE_BUTTON = { background: '#589500', color: '#fff', border: 0, borderRadius: 4, padding: '6px 16px', cursor: 'pointer' }

const UpgradeToast = ({ names, onUpgrade }) => (
  <div className='map-deep-link-upgrade'>
    <p style={{ margin: '0 0 8px' }}>{names} {names.includes(', ') ? 'need' : 'needs'} BaseMap Pro.</p>
    <button type='button' onClick={onUpgrade} style={UPGRADE_BUTTON}>Upgrade</button>
  </div>
)

// Turns the link's layers on through the layer menu's rules, and says what it couldn't do.
export const applyDeepLinkLayers = layerIds => dispatch => {
  const results = layerIds.map(layerId => dispatch(runSetLayerVisibility({ layer_id: layerId, visible: true })))
  const locked = results.filter(r => r.outcome === 'subscription_required')
  const missing = results.filter(r => r.outcome === 'not_found')
  if (locked.length) {
    const names = locked.map(r => r.layerName).join(', ')
    toast(({ closeToast }) => (
      <UpgradeToast names={names} onUpgrade={() => { closeToast(); dispatch(openModal({ subInfo: true })) }} />
    ), { autoClose: false })
  }
  if (missing.length) toast(`${missing.length === 1 ? 'A layer in this link isn’t' : 'Some layers in this link aren’t'} available on your map.`)
  return results
}

export const openMapDeepLink = link => async (dispatch, getState) => {
  if (!link) return
  const map = await waitForMap(getState)
  if (!map) return

  if (link.layerIds.length) dispatch(applyDeepLinkLayers(link.layerIds))
  if (link.view) moveMap(map, link.view)

  if (!link.marker && !link.shape) return
  const found = await findAfterSync(dispatch, getState, link)
  if (!found) {
    toast(`That ${link.marker ? 'marker' : 'shape'} isn’t on your map. It may have been deleted, or saved to another account.`)
    return
  }
  const view = objectView(found)
  if (view) {
    moveMap(map, view)
    await whenIdle(map)
  }
  // The renderers draw objects on the next store update; take the item fresh so its mapObj is set.
  await openObject(findLinkedObject(getState(), link) || found)
}
